<template>
  <ValidationObserver ref="poolsForm" v-slot="{ handleSubmit }">
    <b-form @submit.prevent="handleSubmit(onSubmit)" >
      <div class="d-flex justify-content-between mb-4">
        <h3>
          {{ pid ? $t("edit_crd_distribution_pool") : $t("create_crd_distribution_pool") | upperfirst }}
        </h3>
        <!-- Action Buttons -->
        <div class="d-flex justify-content-end my-3">
          <b-button
            class="mr-2"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("back_to_distribution") | capitalize }}
          </b-button>
          <submit-button variant="secondary">
            {{ $t("save") | capitalize }}
          </submit-button>
        </div>
      </div>

      <!-- 1st Row -->
      <b-row>
        <!-- Name -->
        <b-col cols="6">
          <form-field
            name="name"
            :label="$t('name')"
            type="text"
            rules="required"
            v-model="form.name"
          />
        </b-col>
        <!-- Society -->
        <b-col cols="6" class="pb-0 m-0">
          <society-select :value="form.society" @search="onSocietySearch" rules="required" />
        </b-col>
      </b-row>

      <!-- 2nd Row -->
      <b-row>
        <!-- Code -->
        <b-col cols="3">
          <form-field
            name="code"
            :label="$t('code')"
            type="text"
            maxlength="20"
            rules="required|alphaNum"
            v-model="form.code"
            upper
          />
        </b-col>
        <!-- Fund ID -->
        <b-col cols="3">
          <form-field
            name="fundInternalId"
            :label="$t('fund_id')"
            type="text"
            v-model="form.fundInternalId"
          />
        </b-col>

        <!-- Start date -->
        <b-col cols="3">
          <date-field
            :label="$t('start_date')"
            name="startDate"
            v-model="form.startDate"
            class="w-50 mr-3"
          />
        </b-col>

        <!-- End date -->
        <b-col cols="3">
          <date-field
            :label="$t('end_date')"
            name="endDate"
            v-model="form.endDate"
            :disabled="!form.startDate"
            :disabled-date="beforeStartDate"
            class="w-50"
          />
        </b-col>

      </b-row>

      <!-- 3rd Row -->
      <b-row>
        <!-- Bank Amount -->
        <b-col cols="3">
          <form-field
            name="grossAmount"
            :label="$t('bank_amount')"
            type="number"
            step="0.01"
            min="0"
            v-model="form.grossAmount"
          />
        </b-col>
         <!-- Deductibles -->
        <b-col>
          <label for="deductibles" class="top-label">{{ $t('deductibles') | startcase }}:</label>
          <type-and-value-input
            v-if="form.deductiblesUnit !== null"
            :item="deductiblesItem"
            :itemTypes="amountTypeOptions"
            @input="onDeductiblesInput"
            name="deductibles"
            class="top-label"
            type="number"
            step="0.01"
            min="0"
            :max="getMaxValue(form.deductiblesUnit)"
            :helpText="form.deductiblesHelpText"
          />
        </b-col>
        <!-- Admin Costs -->
        <b-col>
          <label for="administrationCosts" class="top-label">{{ $t('administration_costs') | startcase }}:</label>
          <type-and-value-input
            v-if="form.administrationCostsUnit !== null"
            :item="administrationCostsItem"
            :itemTypes="amountTypeOptions"
            @input="onAdministrationCostsInput"
            name="administrationCosts"
            class="top-label"
            type="number"
            step="0.01"
            min="0"
            :max="getMaxValue(form.administrationCostsUnit)"
            :helpText="form.administrationCostsHelpText"
          />
        </b-col>
        <!-- Net Distributable Amount -->
        <b-col cols="3">
          <div class="d-flex align-items-center px-0 mx-0 w-100">
            <form-field
              name="netDistributableAmount"
              :label="$t('net_distributable_amount')"
              type="number"
              step="0.01"
              min="0"
              v-model="form.netDistributableAmount"
              :disabled="computeNetDistributableAmount"
              class="w-100"
            />
            <action
              :title="$t('edit')"
              @click="onEditNetDistributableAmount"
              :icon="['fas', 'pen']"
              class="ml-2 mt-2"
            />
          </div>
        </b-col>
      </b-row>

      <!-- 4th Row -->
      <b-row>
        <b-col>
           <label>
          {{ $t('crd_files') | upperfirst }}:
          </label>
          <!-- File selector -->
            <files-selector
              :initial="form.crdFiles"
              :externalFilters="fileFilters"
              :fileOnlySelection="true"
              :fileType="selectableFileTypes.CRD"
              @select-files="onSelectFiles"
            />
        </b-col>
      </b-row>

      <!-- Exclusion File -->
      <div class="d-flex flex-column align-items-center justify-content-center mt-3 pt-3">
        <div class="d-flex flex-nowrap justify-content-start align-items-center"><p class="text-muted">{{ $t("exclusions") | capitalize }}</p></div>
        <div v-if="form.exclusionFile" class="text-center text-info d-flex">
          <span class="download-link" @click="downloadExclusionFile">{{ form.exclusionFile }}</span>
          <action
            :title="$t('delete')"
            @click="onDeleteExclusionFile"
            :icon="['fas', 'trash']"
            color="red"
          />
        <modal
          :id="'delete-exclusion-file-modal'"
          :title="$t('delete_exclusion_file') | capitalize"
          :okTitle="$t('yes') | capitalize"
          :cancelTitle="$t('no') | capitalize"
          @ok="onConfirmDeleteExclusionFile"
        >
          <div>{{ $t("confirm_delete_exclusion_file") | capitalize }}?</div>
        </modal>
        </div>
        <div v-else class="d-flex mt-3 mb-3 justify-content-start">
          <b-button variant="secondary" @click="onUploadExclusionFile">{{ $t("upload_exclusion_file") | capitalize }}</b-button>
        </div>
        <file-upload-modal
          :title="$t('upload_exclusion_file')"
          id="exclusion-file-upload"
          :uploadUrl="exclusionFileUploadUrl"
          :templateUrl="$config.ATLAS_EXCLUSIONS_TEMPLATE_URL"
          @exclusion-file-upload="onExclusionFileUpload"
        />
      </div>

    </b-form>
  </ValidationObserver>
</template>

<style lang="scss" scoped>
label {
  font-weight: bold;
  font-size: 0.85rem;
}

.top-label {
  font-size: 0.85rem;
}
</style>

<script>
import { distributionPoolRuleTypes, fundUnitTypes, poolsFormType, selectableFileTypes } from "@/constants"
import { ValidationObserver } from "vee-validate"
import { clickOnUrl } from "@/services/utils"
import { mapMutations } from "vuex"
import moment from "moment"

const UNIT_TYPES = {
  A: "A",
  P: "P",
}

export default {
  name: "CRDPoolsForm",
  components: {
    ValidationObserver
  },
  data () {
    return {
      computeNetDistributableAmount: false,
      amountTypeOptions: [
        fundUnitTypes.P,
        fundUnitTypes.A,
      ],
      form: {
        name: "",
        code: "",
        fundInternalId: "",
        grossAmount: null,
        deductibles: null,
        deductiblesPercent: 0,
        deductiblesUnit: null,
        deductiblesHelpText: null,
        deductiblesUnitsHaveChanged: false,
        administrationCosts: null,
        administrationCostsPercent: 0,
        administrationCostsUnit: null,
        administrationCostsHelpText: null,
        administrationCostsUnitsHaveChanged: false,
        netDistributableAmount: null,
        distributionPoolRules: [{ 0: distributionPoolRuleTypes.CRD }],
        crdFiles: [],
        exclusionFile: "",
        society: null,
        startDate: null,
        endDate: null,
      },
      fileFilters: null,
      selectableFileTypes,
    }
  },
  computed: {
    administrationCostsItem () {
      let type = this.form.administrationCostsUnit
      let value = type === UNIT_TYPES.P ? this.form.administrationCostsPercent : this.form.administrationCosts

      return {
        type: fundUnitTypes[type],
        value,
      }
    },
    deductiblesItem () {
      let type = this.form.deductiblesUnit
      let value = type === UNIT_TYPES.P ? this.form.deductiblesPercent : this.form.deductibles

      return {
        type: fundUnitTypes[type],
        value,
      }
    },
    exclusionFileUploadUrl () {
      return `/distributions/distributions/${this.id}/pools/${this.pid}/exclusion_file/`
    },
  },
  methods: {
    ...mapMutations("alert", ["success", "error"]),
    beforeStartDate (date) {
      return date < moment(this.form.startDate)
    },
    onChange () {
      this.$nextTick(() => {
        this.$emit("input", this.form)
      })
    },
    onSelectFiles (crdFiles) {
      this.form = { ...this.form, crdFiles }
    },
    onAdministrationCostsInput (administrationCosts) {
      let { type } = administrationCosts
      if (
        (type === fundUnitTypes.P && this.form.administrationCostsUnit !== UNIT_TYPES.P)
        || (type === fundUnitTypes.A && this.form.administrationCostsUnit !== UNIT_TYPES.A)
      ) {
        administrationCosts.value = 0
        this.administrationCostsUnitsHaveChanged = true
      }

      if (type === fundUnitTypes.P) {
        this.form.administrationCostsUnit = UNIT_TYPES.P
        this.form.administrationCostsPercent = administrationCosts.value
      } else {
        this.form.administrationCostsUnit = UNIT_TYPES.A
        this.form.administrationCosts = administrationCosts.value
      }
    },
    onDeductiblesInput (deductibles) {
      let { type } = deductibles
      if (
        (type === fundUnitTypes.P && this.form.deductiblesUnit !== UNIT_TYPES.P)
        || (type === fundUnitTypes.A && this.form.deductiblesUnit !== UNIT_TYPES.A)
      ) {
        deductibles.value = 0
        this.deductiblesUnitsHaveChanged = true
      }

      if (type === fundUnitTypes.P) {
        this.form.deductiblesUnit = UNIT_TYPES.P
        this.form.deductiblesPercent = deductibles.value
      } else {
        this.form.deductiblesUnit = UNIT_TYPES.A
        this.form.deductibles = deductibles.value
      }
    },
    onEditNetDistributableAmount () {
      this.form.deductibles = 0
      this.form.deductiblesPercent = 0
      this.form.deductiblesUnit = UNIT_TYPES.A
      this.form.deductiblesHelpText = null
      this.form.administrationCosts = 0
      this.form.administrationCostsPercent = 0
      this.form.administrationCostsUnit = UNIT_TYPES.A
      this.form.administrationCostsHelpText = null
      this.computeNetDistributableAmount = false
    },
    onUploadExclusionFile () {
      this.$bvModal.show("exclusion-file-upload")
    },
    onDeleteExclusionFile () {
      this.$bvModal.show("delete-exclusion-file-modal")
    },
    onConfirmDeleteExclusionFile () {
      this.$api.distributions.distributionsPoolExclusionFileDelete(this.id, this.pid)
        .then(() => {
          this.success(this.$t("exclusion_file_deleted"))
          this.form.exclusionFile = ""
        }).catch(error => {
          if (error.response.data && error.response.data.detail) this.error(error.response.data.detail)
        })
    },
    downloadExclusionFile () {
      this.$api.distributions.distributionPoolsDownloadExclusionFile(this.id, this.pid)
        .then(({ data }) => {
          const { url } = data
          clickOnUrl(url)
        })
        .catch((err) => {
          let message = err.response.data?.message || err.response.data || err.response
          this.error(message)
        })
    },
    onExclusionFileUpload (uploadedFile) {
      this.form.exclusionFile = uploadedFile
    },
    getMaxValue (unit) {
      if (unit === UNIT_TYPES.P) return 100.00
    },
    convertPercentToAmount (percentValue) {
      return this.form.grossAmount * percentValue/100
    },
    onSubmit () {
      let data = { ...this.form }
      let { distributionPoolsCreate, distributionPoolsUpdate } =  this.$api.distributions
      data.crdFiles = data.crdFiles.map(f => f.id)

      let call = this.pid ? distributionPoolsUpdate(this.id, this.pid, data, poolsFormType.CRD) : distributionPoolsCreate(this.id, data, poolsFormType.CRD)

      call
        .then(() => {
          if (this.pid) {
            // On edit: Show success toast.
            this.success(this.$t("distribution_pool_successfully_edited"))
            this.loadData()
          } else {
            // On create: Show success toast and redirect.
            this.success(this.$t("distribution_pool_successfully_created"))
            this.$router.go(-1)
          }
        })
        .catch(error => {
          const { data } = error.response
          if (data?.length) this.error(data[0])
          this.$refs.poolsForm.setErrors(data)
        })
    },
    onSocietySearch (newValue) {
      this.form.society = newValue
    },
    async loadData () {
      const response = await this.$api.distributions.distributionPoolsRetrieve(this.id, this.pid, poolsFormType.CRD)

      this.form = {
        ...response.data,
        crdFiles: response.data.crdFiles || [],
      }
    },
  },
  watch: {
    "form.society" (value) {
      this.fileFilters = { remitting_society: value }
    },
    "form": {
      deep: true,
      handler: function (formData) {
        let administrationCostsAmount = formData.administrationCosts
        let deductiblesAmount = formData.deductibles

        if (formData.administrationCostsUnit === UNIT_TYPES.P) {
          administrationCostsAmount = this.convertPercentToAmount(formData.administrationCostsPercent)
          this.form.administrationCostsHelpText = administrationCostsAmount.toString()
        } else {
          this.form.administrationCostsHelpText = ""
        }

        if (formData.deductiblesUnit === UNIT_TYPES.P) {
          deductiblesAmount = this.convertPercentToAmount(formData.deductiblesPercent)
          this.form.deductiblesHelpText = deductiblesAmount.toString()
        } else {
          this.form.deductiblesHelpText = ""
        }

        this.computeNetDistributableAmount = Boolean(parseFloat(administrationCostsAmount)) || Boolean(parseFloat(deductiblesAmount)) || this.administrationCostsUnitsHaveChanged || this.deductiblesUnitsHaveChanged

        if (this.computeNetDistributableAmount) {
          let netDistributableAmount = formData.grossAmount - administrationCostsAmount - deductiblesAmount
          this.form.netDistributableAmount = netDistributableAmount.toFixed(2)
        }
      }
    },
  },
  async mounted () {
    // Retrieve a pool if a pool ID is passed as prop
    if (this.pid) {
      this.loadData()
    } else {
      this.form = {
        ...this.form,
        deductiblesUnit: UNIT_TYPES.A,
        administrationCostsUnit: UNIT_TYPES.A,
        deductibles: 0,
        administrationCosts: 0
      }
    }
  },
  props: {
    id: String, // Distrbution ID
    pid: String, // Pool ID,
  }
}
</script>
